/**
 * FAQs Store
 * Manages the state of FAQs with caching and filtering capabilities
 */
import { create } from 'zustand';
import { FaqService } from '../services/faq.service';
import { Faq, FaqCategory, FaqFilterOptions } from '../types/faqs.types';

// Cache duration in milliseconds (5 minutes)
const CACHE_DURATION = 5 * 60 * 1000;

interface FaqsState {
  // Data
  faqs: Faq[];
  categories: FaqCategory[];

  // UI state
  isLoading: boolean;
  error: string | null;
  lastFetched: number | null;

  // Filter options
  filterOptions: FaqFilterOptions;
  filteredFaqs: Faq[];

  // Actions
  fetchFaqs: () => Promise<void>;
  createFaq: (data: {
    title: string;
    description: string;
    categories: string[];
  }) => Promise<Faq>;
  updateFaq: (
    id: string,
    data: Partial<{ title: string; description: string; categories: string[] }>
  ) => Promise<Faq>;
  deleteFaq: (id: string) => Promise<void>;
  setFilterOptions: (options: Partial<FaqFilterOptions>) => void;
  resetFilters: () => void;
}

/**
 * Zustand store for FAQs
 */
export const useFaqsStore = create<FaqsState>((set, get) => ({
  // Initial state
  faqs: [],
  categories: [],
  isLoading: false,
  error: null,
  lastFetched: null,
  filterOptions: {
    searchTerm: '',
    selectedCategory: null,
  },
  filteredFaqs: [],

  /**
   * Fetch FAQs from API with caching
   */
  fetchFaqs: async () => {
    const { lastFetched, faqs } = get();
    const now = Date.now();

    // Return cached data if it's still valid
    if (lastFetched && now - lastFetched < CACHE_DURATION && faqs.length > 0) {
      // Apply current filters to cached data
      set((state) => ({
        filteredFaqs: filterFaqs(state.faqs, state.filterOptions),
      }));
      return;
    }

    // Otherwise fetch fresh data
    set({ isLoading: true, error: null });

    try {
      const response = await FaqService.getAllFaqs();

      const faqs = Array.isArray(response.data) ? response.data : [];
      const categories = Array.isArray(response.categories)
        ? response.categories
        : [];

      set((state) => ({
        faqs,
        categories,
        lastFetched: Date.now(),
        filteredFaqs: filterFaqs(faqs, state.filterOptions),
        isLoading: false,
      }));
    } catch (error) {
      console.error('Error fetching FAQs:', error);
      set({
        error: error instanceof Error ? error.message : 'Failed to fetch FAQs',
        isLoading: false,
      });
    }
  },

  /**
   * Create a new FAQ
   */
  createFaq: async (data) => {
    set({ isLoading: true, error: null });

    try {
      const newFaq = await FaqService.createFaq(data);

      set((state) => {
        // Add new FAQ to the list
        const updatedFaqs = [...state.faqs, newFaq];

        // Update categories if needed
        const newCategories = [...state.categories];
        data.categories.forEach((category) => {
          if (!newCategories.includes(category)) {
            newCategories.push(category);
          }
        });

        return {
          faqs: updatedFaqs,
          categories: newCategories,
          filteredFaqs: filterFaqs(updatedFaqs, state.filterOptions),
          isLoading: false,
        };
      });

      return newFaq;
    } catch (error) {
      console.error('Error creating FAQ:', error);
      set({
        error: error instanceof Error ? error.message : 'Failed to create FAQ',
        isLoading: false,
      });
      throw error;
    }
  },

  /**
   * Update an existing FAQ
   */
  updateFaq: async (id, data) => {
    set({ isLoading: true, error: null });

    try {
      const updatedFaq = await FaqService.updateFaq(id, data);

      set((state) => {
        // Replace the updated FAQ in the list
        const updatedFaqs = state.faqs.map((faq) =>
          faq.id === id ? updatedFaq : faq
        );

        // Update categories if needed
        let newCategories = [...state.categories];
        if (data.categories) {
          data.categories.forEach((category) => {
            if (!newCategories.includes(category)) {
              newCategories.push(category);
            }
          });
        }

        return {
          faqs: updatedFaqs,
          categories: newCategories,
          filteredFaqs: filterFaqs(updatedFaqs, state.filterOptions),
          isLoading: false,
        };
      });

      return updatedFaq;
    } catch (error) {
      console.error('Error updating FAQ:', error);
      set({
        error: error instanceof Error ? error.message : 'Failed to update FAQ',
        isLoading: false,
      });
      throw error;
    }
  },

  /**
   * Delete a FAQ
   */
  deleteFaq: async (id) => {
    set({ isLoading: true, error: null });

    try {
      await FaqService.deleteFaq(id);

      set((state) => {
        // Remove the deleted FAQ from the list
        const updatedFaqs = state.faqs.filter((faq) => faq.id !== id);

        // Recalculate categories from remaining FAQs
        const allCategories = updatedFaqs.flatMap((faq) => faq.categories);
        const uniqueCategories = [...new Set(allCategories)];

        return {
          faqs: updatedFaqs,
          categories: uniqueCategories,
          filteredFaqs: filterFaqs(updatedFaqs, state.filterOptions),
          isLoading: false,
        };
      });
    } catch (error) {
      console.error('Error deleting FAQ:', error);
      set({
        error: error instanceof Error ? error.message : 'Failed to delete FAQ',
        isLoading: false,
      });
      throw error;
    }
  },

  /**
   * Set filter options and apply filters
   */
  setFilterOptions: (options) => {
    set((state) => {
      const newFilterOptions = {
        ...state.filterOptions,
        ...options,
      };

      return {
        filterOptions: newFilterOptions,
        filteredFaqs: filterFaqs(state.faqs, newFilterOptions),
      };
    });
  },

  /**
   * Reset all filters
   */
  resetFilters: () => {
    set((state) => {
      const resetOptions = {
        searchTerm: '',
        selectedCategory: null,
      };

      return {
        filterOptions: resetOptions,
        filteredFaqs: filterFaqs(state.faqs, resetOptions),
      };
    });
  },
}));

/**
 * Helper function to filter FAQs based on filter options
 */
function filterFaqs(faqs: Faq[], filterOptions: FaqFilterOptions): Faq[] {
  const { searchTerm, selectedCategory } = filterOptions;

  return faqs.filter((faq) => {
    // Filter by category if selected
    if (selectedCategory && !faq.categories.includes(selectedCategory)) {
      return false;
    }

    // Filter by search term if provided
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      return (
        faq.title.toLowerCase().includes(searchLower) ||
        faq.description.toLowerCase().includes(searchLower)
      );
    }

    return true;
  });
}
